import React, { useState, useEffect } from 'react';

/**
 * Props interface for Intro component
 */
interface IntroProps {
  onWorkplacesClick?: () => void;
}

const baseName = 'Jules Lye';
const phrases = [
  'would love to meet you!',
  'loves code and coffee.',
  '- peace & love & code.',
  'builds beautiful websites.',
  'sometimes builds guitar pedals too.',
  'is super keen to connect.',
  'creates exceptional experiences.',
  'lives in Brunswick, Melbourne.'
];

/**
 * Intro Component
 * 
 * This component handles the main hero section with typewriter animation.
 * Features:
 * - Blinking cursor
 * - Typewriter effect that types phrases after name
 * - Backspace animation returning to base name
 */
const Intro: React.FC<IntroProps> = ({ onWorkplacesClick }) => {
  const [displayText, setDisplayText] = useState('');


  useEffect(() => {
    let currentPhraseIndex = 0;
    let timeoutId: NodeJS.Timeout;

    const typePhrase = (phrase: string, currentIndex: number = 0) => {
      if (currentIndex <= phrase.length) {
        setDisplayText(baseName + ' ' + phrase.substring(0, currentIndex));
        timeoutId = setTimeout(() => typePhrase(phrase, currentIndex + 1), 80);
      } else {
        // Phrase fully typed, wait 10 seconds
        timeoutId = setTimeout(() => backspacePhrase(phrase, phrase.length), 10000);
      }
    };

    const backspacePhrase = (phrase: string, currentIndex: number) => {
      if (currentIndex >= 0) {
        setDisplayText(baseName + ' ' + phrase.substring(0, currentIndex));
        timeoutId = setTimeout(() => backspacePhrase(phrase, currentIndex - 1), 40);
      } else {
        // Back to base name, wait 10 seconds then start next phrase
        setDisplayText(baseName);
        currentPhraseIndex = (currentPhraseIndex + 1) % phrases.length;
        timeoutId = setTimeout(() => {
          typePhrase(phrases[currentPhraseIndex]);
        }, 10000);
      }
    };

    // Initial delay before first phrase
    setDisplayText(baseName);
    timeoutId = setTimeout(() => {
      typePhrase(phrases[0]);
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, []);

  return (
    <div className="intro mx-auto mb-16 text-left">
      <div className="max-w-[1040px]">
        {/* Title with Typewriter Effect */}
        <div id="jules" className="mb-8 min-h-[60px] md:min-h-[30px]">
          <h1 className="text-2xl font-medium text-jules-primary dark:text-jules-primary-dark leading-tight">
            <span aria-hidden="true">
              {displayText}
              <span className="cursor animate-blink">|</span>
            </span>
            <span className="sr-only">Jules Lye</span>
          </h1>
        </div>

        {/* Tagline */}
        <p className="text-2xl font-thin mb-8 leading-relaxed">
          <span className="block text-gray-400">
            Frontend Developer & Designer
          </span>
        </p>

        {/* Main description paragraphs */}
        <div className="space-y-6 mb-8">
          <p className="text-xl md:text-2xl font-thin text-jules-primary dark:text-jules-primary-dark leading-relaxed">
            Hi, I'm Jules, a Website Producer and Developer. For 13 years, I've been delivering exceptional digital experiences at Involved, a digital agency in Collingwood, Melbourne.
          </p>

          <p className="text-xl md:text-2xl font-thin text-jules-primary dark:text-jules-primary-dark leading-relaxed">
            From technical strategy to frontend development, I specialise in creating accessible, high-performing websites. I love the intersection of beautiful design and clean code - focusing on both the little details and the project’s bigger picture.
          </p>

          <p className="text-xl md:text-2xl font-thin text-jules-primary dark:text-jules-primary-dark leading-relaxed">
            I've worked with amazing brands in tourism, retail, the arts, and beyond - from Accor to ANCAP Car Safety. Before digital, I started out in television production and spent 10 years in music retail - a mix that gave me both a creative industry foundation and a solid understanding of what businesses need to thrive online.
          </p>

          {/* Final paragraph with link to workplaces */}
          <p className="text-xl md:text-2xl font-thin text-jules-primary dark:text-jules-primary-dark leading-relaxed">
            When I'm not working on the web, you'll find me with my family in Brunswick, playing guitar, sketching, or exploring Melbourne's music scene. Want to know more about my{' '}
            <button
              type="button"
              className="workplaces underline cursor-pointer hover:text-jules-accent dark:hover:text-jules-accent-dark transition-colors bg-transparent border-0 p-0 text-inherit font-inherit inline"
              onClick={onWorkplacesClick}
              aria-label="Scroll down to Jules's work experience section"
            >
              experience
            </button>
            ?
          </p>
        </div>
      </div>
    </div>
  );
};

export default Intro;